import React, { useState, useRef, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useCreateTenant, useUpdateTenant } from 'hooks/api';
import type { TenantResponse, CreateTenantRequest, UpdateTenantRequest } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface TenantFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  tenant: TenantResponse | null;
  onClose: () => void;
  onSaveSuccess: () => void;
  tabId?: string;
  moduleName?: string;
  /** Called when user clicks minimize — only hides the modal, does NOT clear parent state */
  onMinimize?: () => void;
  /** Called by the minimized strip to reopen this modal with its previous form state intact */
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, tenantCode?: string) => void;
  createInstanceId?: string; // Unique ID for each Add session — enables multiple minimized Add modals
}

interface TenantFormData {
  tenantName: string;
  tenantCode: string;
  tenantDisplayName: string;
  tenantDescription: string;
  tenantCategory: string;
  keycloakRealmType: string;
  isTrial: boolean;
  trialEndDate: string;
  subscriptionPlan: string;
  maxUsers: string;
  maxStorageGb: string;
  logoBase64: string;
}

const INITIAL_FORM_DATA: TenantFormData = {
  tenantName: '',
  tenantCode: '',
  tenantDisplayName: '',
  tenantDescription: '',
  tenantCategory: 'external',
  keycloakRealmType: 'shared',
  isTrial: false,
  trialEndDate: '',
  subscriptionPlan: '',
  maxUsers: '',
  maxStorageGb: '',
  logoBase64: '',
};

// ============================================================================
// COMPONENT
// ============================================================================

const TenantFormModal: React.FC<TenantFormModalProps> = ({
  isOpen,
  mode,
  tenant,
  onClose,
  onSaveSuccess,
  tabId,
  moduleName,
  onMinimize,
  onRestoreModal,
  createInstanceId,
}) => {
  const { createTenant, loading: creating } = useCreateTenant();
  const { updateTenant, loading: updating } = useUpdateTenant();
  const { minimize } = useModalMinimize();
  const formDataRef = useRef<TenantFormData>(INITIAL_FORM_DATA);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  // When true, the next isOpen=true is a restore — skip form repopulation to preserve user edits
  const isRestoreRef = useRef(false);

  const [formData, setFormData] = useState<TenantFormData>(INITIAL_FORM_DATA);
  formDataRef.current = formData;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const initialSnapshotRef = useRef('');

  // Populate form when opening — skip on restore to preserve user edits
  useEffect(() => {
    if (isOpen) {
      if (isRestoreRef.current) {
        // Restoring from minimized state — keep existing formData, just clear errors
        isRestoreRef.current = false;
        setFieldErrors({});
        return;
      }
      if (mode === 'edit' && tenant) {
        const data: TenantFormData = {
          tenantName: tenant.tenantName || '',
          tenantCode: tenant.tenantCode || '',
          tenantDisplayName: tenant.tenantDisplayName || '',
          tenantDescription: tenant.tenantDescription || '',
          tenantCategory: tenant.tenantCategory || 'external',
          keycloakRealmType: tenant.keycloakRealmType || 'shared',
          isTrial: tenant.isTrial || false,
          trialEndDate: tenant.trialEndDate ? tenant.trialEndDate.split('T')[0] : '',
          subscriptionPlan: tenant.subscriptionPlan || '',
          maxUsers: tenant.maxUsers ? String(tenant.maxUsers) : '',
          maxStorageGb: tenant.maxStorageGb ? String(tenant.maxStorageGb) : '',
          logoBase64: tenant.logoBase64 || '',
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
      } else {
        setFormData({ ...INITIAL_FORM_DATA });
        initialSnapshotRef.current = JSON.stringify(INITIAL_FORM_DATA);
      }
      setFieldErrors({});
    }
  }, [isOpen, mode, tenant]);

  const getFieldError = useCallback((field: string) => fieldErrors[field] || '', [fieldErrors]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleFieldChange = useCallback((field: keyof TenantFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearFieldError(field);
  }, [clearFieldError]);

  // ---- Validation ----
  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.tenantName.trim()) errors.tenantName = 'Tenant name is required';
    else if (formData.tenantName.length < 3) errors.tenantName = 'Minimum 3 characters';
    else if (formData.tenantName.length > 100) errors.tenantName = 'Maximum 100 characters';

    if (mode === 'create') {
      if (!formData.tenantCode.trim()) errors.tenantCode = 'Tenant code is required';
      else if (formData.tenantCode.length < 3) errors.tenantCode = 'Minimum 3 characters';
      else if (!/^[a-z0-9-]+$/.test(formData.tenantCode)) errors.tenantCode = 'Only lowercase letters, numbers, and hyphens';
    }

    if (!formData.tenantCategory) errors.tenantCategory = 'Category is required';
    if (!formData.keycloakRealmType) errors.keycloakRealmType = 'Realm type is required';

    if (formData.isTrial && !formData.trialEndDate) errors.trialEndDate = 'Trial end date is required when trial is enabled';

    if (formData.maxUsers && (isNaN(Number(formData.maxUsers)) || Number(formData.maxUsers) < 1 || Number(formData.maxUsers) > 10000)) {
      errors.maxUsers = 'Must be between 1 and 10,000';
    }

    if (formData.maxStorageGb && (isNaN(Number(formData.maxStorageGb)) || Number(formData.maxStorageGb) < 1 || Number(formData.maxStorageGb) > 1000)) {
      errors.maxStorageGb = 'Must be between 1 and 1,000';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, mode]);

  // ---- Minimize ----
  const handleMinimize = useCallback(() => {
    const capturedMode = modeRef.current;
    const currentCreateInstanceId = createInstanceId;
    const id = capturedMode === 'create'
      ? `${tabId || 'tenant'}-form-modal-create-${currentCreateInstanceId || 'default'}`
      : `${tabId || 'tenant'}-form-modal-edit-${tenant?.tenantCode || 'unknown'}`;
    let title: string;
    if (capturedMode === 'create') {
      const code = formDataRef.current.tenantCode?.trim();
      const name = formDataRef.current.tenantName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit — ${formDataRef.current.tenantName || tenant?.tenantCode || ''}`;
    }
    // Capture this session's data at minimize time — a fresh open for another
    // session will overwrite formData, so we must restore from captured copies.
    const savedFormData = { ...formDataRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedFormData) !== savedSnapshot : false;
    minimize({
      id,
      title,
      moduleName: moduleName || 'Tenants',
      tabId: tabId || 'tenant-master',
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          capturedMode,
          capturedMode === 'create' ? currentCreateInstanceId : undefined,
          capturedMode === 'edit' ? tenant?.tenantCode : undefined,
        );
      },
    });
    if (onMinimize) {
      onMinimize();               // hide without clearing parent state (selectedTenant preserved)
    } else {
      onClose();                  // fallback if onMinimize not provided
    }
  }, [tabId, moduleName, tenant, minimize, onClose, onMinimize, onRestoreModal, createInstanceId]);

  // ---- Save ----
  const handleSaveClick = useCallback(() => {
    if (!validate()) return;
    setConfirmSaveOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);

    try {
      if (mode === 'create') {
        const request: CreateTenantRequest = {
          tenantName: formData.tenantName.trim(),
          tenantCode: formData.tenantCode.trim(),
          tenantDisplayName: formData.tenantDisplayName.trim() || undefined,
          tenantDescription: formData.tenantDescription.trim() || undefined,
          tenantCategory: formData.tenantCategory as 'internal' | 'external',
          keycloakRealmType: formData.keycloakRealmType as 'shared' | 'dedicated',
          isTrial: formData.isTrial,
          trialEndDate: formData.trialEndDate || undefined,
          logoBase64: formData.logoBase64 || undefined,
        };
        await createTenant(request, {
          onSuccess: () => {
            notificationService.toast.success(`Tenant "${formData.tenantName}" created successfully.`);
            onSaveSuccess();
          },
        });
      } else {
        if (!tenant) return;
        const request: UpdateTenantRequest = {
          tenantName: formData.tenantName.trim(),
          tenantDisplayName: formData.tenantDisplayName.trim() || undefined,
          tenantDescription: formData.tenantDescription.trim() || undefined,
          tenantCategory: formData.tenantCategory || undefined,
          keycloakRealmType: formData.keycloakRealmType || undefined,
          subscriptionPlan: formData.subscriptionPlan || undefined,
          maxUsers: formData.maxUsers ? Number(formData.maxUsers) : undefined,
          maxStorageGb: formData.maxStorageGb ? Number(formData.maxStorageGb) : undefined,
          trialEndDate: formData.trialEndDate || undefined,
        };
        await updateTenant(tenant.tenantId, request, {
          onSuccess: () => {
            notificationService.toast.success(`Tenant "${formData.tenantName}" updated successfully.`);
            onSaveSuccess();
          },
        });
      }
    } catch {
      notificationService.toast.error(`Failed to ${mode} tenant. Please try again.`);
    }
  }, [mode, formData, tenant, createTenant, updateTenant, onSaveSuccess]);

  // ---- Close with unsaved check ----
  // Read from refs to avoid stale closure (create mode bails out of re-render with same ref)
  const handleCloseAttempt = useCallback(() => {
    const dirty = JSON.stringify(formDataRef.current) !== initialSnapshotRef.current;
    if (dirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  const isSaving = creating || updating;

  return (
    <>
      <div className="modal-backdrop" onClick={handleCloseAttempt} />
      <div className="modal-container modal-lg">
        {/* Loading overlay */}
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'create' ? 'Creating tenant...' : 'Updating tenant...'}
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.home size={20} />
            {mode === 'create' ? 'Create New Tenant' : `Edit Tenant: ${tenant?.tenantCode}`}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleCloseAttempt} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Row: Tenant Name | Tenant Code */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Tenant Name *</label>
              <input
                type="text"
                className={`form-input ${getFieldError('tenantName') ? 'form-field-error' : ''}`}
                value={formData.tenantName}
                onChange={(e) => handleFieldChange('tenantName', e.target.value)}
                placeholder="Enter tenant name"
                maxLength={100}
              />
              {getFieldError('tenantName') && <div className="form-error-message">{getFieldError('tenantName')}</div>}
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Tenant Code {mode === 'create' ? '*' : ''}</label>
              <input
                type="text"
                className={`form-input ${getFieldError('tenantCode') ? 'form-field-error' : ''}`}
                value={formData.tenantCode}
                onChange={(e) => handleFieldChange('tenantCode', e.target.value.toLowerCase())}
                placeholder="e.g. acme-corp"
                maxLength={50}
                disabled={mode === 'edit'}
              />
              {getFieldError('tenantCode') && <div className="form-error-message">{getFieldError('tenantCode')}</div>}
              {mode === 'create' && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Lowercase letters, numbers, hyphens only</div>}
            </div>
          </div>

          {/* Row: Display Name | Category */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Display Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.tenantDisplayName}
                onChange={(e) => handleFieldChange('tenantDisplayName', e.target.value)}
                placeholder="Optional display name"
                maxLength={150}
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <SyutSelect
                label="Category *"
                options={[
                  { value: 'internal', label: 'Internal' },
                  { value: 'external', label: 'External' },
                ]}
                value={formData.tenantCategory}
                onChange={(val: string | string[] | null) => handleFieldChange('tenantCategory', val || '')}
                placeholder="Select category"
                error={getFieldError('tenantCategory')}
                isClearable={false}
              />
            </div>
          </div>

          {/* Row: Realm Type | Subscription Plan (edit) OR Realm Type full-width (create) */}
          {mode === 'create' ? (
            <div className="form-group">
              <SyutSelect
                label="Realm Type *"
                options={[
                  { value: 'shared', label: 'Shared' },
                  { value: 'dedicated', label: 'Dedicated' },
                ]}
                value={formData.keycloakRealmType}
                onChange={(val: string | string[] | null) => handleFieldChange('keycloakRealmType', val || '')}
                placeholder="Select realm type"
                error={getFieldError('keycloakRealmType')}
                isClearable={false}
              />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <SyutSelect
                  label="Realm Type *"
                  options={[
                    { value: 'shared', label: 'Shared' },
                    { value: 'dedicated', label: 'Dedicated' },
                  ]}
                  value={formData.keycloakRealmType}
                  onChange={(val: string | string[] | null) => handleFieldChange('keycloakRealmType', val || '')}
                  placeholder="Select realm type"
                  error={getFieldError('keycloakRealmType')}
                  isClearable={false}
                />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Subscription Plan</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.subscriptionPlan}
                  onChange={(e) => handleFieldChange('subscriptionPlan', e.target.value)}
                  placeholder="e.g. Basic, Premium, Enterprise"
                />
              </div>
            </div>
          )}

          {/* Row: Max Users | Max Storage (edit only) */}
          {mode === 'edit' && (
            <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Max Users</label>
                <input
                  type="number"
                  className={`form-input ${getFieldError('maxUsers') ? 'form-field-error' : ''}`}
                  value={formData.maxUsers}
                  onChange={(e) => handleFieldChange('maxUsers', e.target.value)}
                  placeholder="1 - 10,000"
                  min={1}
                  max={10000}
                />
                {getFieldError('maxUsers') && <div className="form-error-message">{getFieldError('maxUsers')}</div>}
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Max Storage (GB)</label>
                <input
                  type="number"
                  className={`form-input ${getFieldError('maxStorageGb') ? 'form-field-error' : ''}`}
                  value={formData.maxStorageGb}
                  onChange={(e) => handleFieldChange('maxStorageGb', e.target.value)}
                  placeholder="1 - 1,000"
                  min={1}
                  max={1000}
                />
                {getFieldError('maxStorageGb') && <div className="form-error-message">{getFieldError('maxStorageGb')}</div>}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.tenantDescription}
              onChange={(e) => handleFieldChange('tenantDescription', e.target.value)}
              placeholder="Optional description"
              rows={2}
              maxLength={500}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Row: Trial Tenant | Trial End Date (conditional) */}
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingTop: '0.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={formData.isTrial}
                  onChange={(e) => handleFieldChange('isTrial', e.target.checked)}
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>Trial Tenant</span>
              </label>
            </div>
            {formData.isTrial ? (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Trial End Date *</label>
                <input
                  type="date"
                  className={`form-input ${getFieldError('trialEndDate') ? 'form-field-error' : ''}`}
                  value={formData.trialEndDate}
                  onChange={(e) => handleFieldChange('trialEndDate', e.target.value)}
                />
                {getFieldError('trialEndDate') && <div className="form-error-message">{getFieldError('trialEndDate')}</div>}
              </div>
            ) : <div style={{ flex: 1 }} />}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCloseAttempt} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'create' ? 'Create Tenant' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save Confirmation */}
      <ConfirmDialog
        open={confirmSaveOpen}
        title={mode === 'create' ? 'Create Tenant' : 'Update Tenant'}
        message={mode === 'create'
          ? `Create tenant "${formData.tenantName}" with code "${formData.tenantCode}"?`
          : `Save changes to tenant "${formData.tenantName}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmSaveOpen(false)}
      />

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setConfirmDiscardOpen(false); onClose(); }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
};

export default TenantFormModal;
